import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Navigation, Loader2, AlertCircle, Store, Clock, Truck } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';

const CustomerLocationMap = ({ storeAddress = "MERIVALE ROAD, Ottawa, ON" }) => {
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [distance, setDistance] = useState(null);
  const [estimatedTime, setEstimatedTime] = useState(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  // Store coordinates (UPLANDS FOOD MARKET on Merivale Road, Ottawa)
  const storeLocation = {
    lat: 45.3476,
    lng: -75.7197
  };

  useEffect(() => {
    detectLocation();
  }, []);

  const detectLocation = () => {
    setLoading(true);
    setError(null);

    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      setLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const userLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        setLocation(userLocation);
        calculateDistance(userLocation);
        setLoading(false);
        initializeMap(userLocation);
      },
      (error) => {
        console.error('Location error:', error);
        let errorMessage = 'Unable to detect your location';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied. Please enable location services.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out';
            break;
        }
        setError(errorMessage);
        setLoading(false);
        // Initialize map with just store location
        initializeMap(null);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // Cache for 5 minutes
      }
    );
  };

  const calculateDistance = (userLoc) => {
    // Haversine formula to calculate distance
    const R = 6371; // Earth's radius in km
    const dLat = (storeLocation.lat - userLoc.lat) * Math.PI / 180;
    const dLng = (storeLocation.lng - userLoc.lng) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(userLoc.lat * Math.PI / 180) * Math.cos(storeLocation.lat * Math.PI / 180) *
      Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distanceKm = R * c;
    
    setDistance(distanceKm.toFixed(1));
    
    // Estimate delivery time (assuming 30 km/h average speed in city + 10 min prep)
    const driveTimeMin = Math.round((distanceKm / 30) * 60);
    const totalTime = driveTimeMin + 10; // Add 10 min for preparation
    setEstimatedTime(totalTime);
  };

  const initializeMap = (userLoc) => {
    if (!mapRef.current || !window.google) return;

    const center = userLoc || storeLocation;
    
    const map = new window.google.maps.Map(mapRef.current, {
      center: center,
      zoom: userLoc ? 13 : 15,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      styles: [
        {
          featureType: "poi",
          elementType: "labels",
          stylers: [{ visibility: "off" }]
        }
      ]
    });

    mapInstanceRef.current = map;

    // Store marker
    new window.google.maps.Marker({
      position: storeLocation,
      map: map,
      title: 'UPLANDS FOOD MARKET',
      icon: {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="40" height="40">
            <circle cx="12" cy="12" r="10" fill="#0a1628"/>
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#10b981"/>
            <circle cx="12" cy="9" r="2.5" fill="white"/>
          </svg>
        `),
        scaledSize: new window.google.maps.Size(40, 40),
        anchor: new window.google.maps.Point(20, 40)
      }
    });

    // User marker (if location available)
    if (userLoc) {
      new window.google.maps.Marker({
        position: userLoc,
        map: map,
        title: 'Your Location',
        icon: {
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32">
              <circle cx="12" cy="12" r="10" fill="#3b82f6" stroke="white" stroke-width="2"/>
              <circle cx="12" cy="12" r="4" fill="white"/>
            </svg>
          `),
          scaledSize: new window.google.maps.Size(32, 32),
          anchor: new window.google.maps.Point(16, 16)
        }
      });

      // Draw route line
      const routePath = new window.google.maps.Polyline({
        path: [userLoc, storeLocation],
        geodesic: true,
        strokeColor: '#3b82f6',
        strokeOpacity: 0.8,
        strokeWeight: 3,
        icons: [{
          icon: { path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW },
          offset: '50%'
        }]
      });
      routePath.setMap(map);

      // Fit bounds to show both markers
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend(userLoc);
      bounds.extend(storeLocation);
      map.fitBounds(bounds, 50);
    }
  };

  const openDirections = () => {
    if (location) {
      const url = `https://www.google.com/maps/dir/${location.lat},${location.lng}/${storeLocation.lat},${storeLocation.lng}`;
      window.open(url, '_blank');
    } else {
      window.open(`https://www.google.com/maps/search/?api=1&query=${storeLocation.lat},${storeLocation.lng}`, '_blank');
    }
  };

  // Load Google Maps script
  useEffect(() => {
    if (window.google) return;

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (location) {
        initializeMap(location);
      } else if (!loading) {
        initializeMap(null);
      }
    };
    document.head.appendChild(script);

    return () => {
      // Cleanup if needed
    };
  }, []);

  return (
    <Card className="overflow-hidden bg-white border border-gray-200 shadow-sm">
      {/* Map Container */}
      <div className="relative">
        <div 
          ref={mapRef} 
          className="w-full h-48 bg-gradient-to-br from-blue-100 to-green-100"
          style={{ minHeight: '180px' }}
        >
          {/* Fallback map visualization if Google Maps not loaded */}
          {!window.google && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-2 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Store className="w-8 h-8 text-emerald-600" />
                </div>
                <p className="text-sm text-gray-600 font-medium">UPLANDS FOOD MARKET</p>
                <p className="text-xs text-gray-500">{storeAddress}</p>
              </div>
            </div>
          )}
        </div>

        {/* Location Badge */}
        {location && (
          <div className="absolute top-2 left-2 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-md flex items-center gap-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
            <span className="text-xs font-medium text-gray-700">Your Location Detected</span>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center">
            <div className="text-center">
              <Loader2 className="w-8 h-8 text-emerald-600 animate-spin mx-auto mb-2" />
              <p className="text-sm text-gray-600">Detecting your location...</p>
            </div>
          </div>
        )}
      </div>

      {/* Info Panel */}
      <div className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            {error ? (
              <div className="flex items-center gap-2 text-amber-600">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm">{error}</span>
              </div>
            ) : location ? (
              <div className="space-y-1">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1.5">
                    <MapPin className="w-4 h-4 text-emerald-600" />
                    <span className="text-sm font-semibold text-gray-800">{distance} km away</span>
                  </div>
                  {estimatedTime && (
                    <div className="flex items-center gap-1.5">
                      <Truck className="w-4 h-4 text-blue-600" />
                      <span className="text-sm text-gray-600">~{estimatedTime} min delivery</span>
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500">From your current location to UPLANDS FOOD MARKET</p>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Store className="w-4 h-4 text-emerald-600" />
                <span className="text-sm text-gray-700">UPLANDS FOOD MARKET - {storeAddress}</span>
              </div>
            )}
          </div>

          <Button
            size="sm"
            onClick={location ? openDirections : detectLocation}
            className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
          >
            {location ? (
              <>
                <Navigation className="w-4 h-4" />
                <span className="hidden sm:inline">Directions</span>
              </>
            ) : (
              <>
                <MapPin className="w-4 h-4" />
                <span className="hidden sm:inline">Detect Location</span>
              </>
            )}
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default CustomerLocationMap;
